<template>
    <ValidationObserver ref="form" v-slot="{ handleSubmit, invalid }">
        <div class="card mb-g">
            <div class="card-body pb-0 px-4">
                <!-- Header -->
                <div class="border-top-0 border-left-0 border-right-0 mb-3">
                    <h4 class="mb-0 text-dark fw-500">Quét căn cước công dân tự động</h4>
                    <p class="text-gray-600 italic">
                        <i>Chụp ảnh cả hai mặt của căn cước để tự động điền đầy đủ thông tin</i>
                    </p>
                </div>

                <!-- Main Content (2 Cột 6 - 6) -->
                <div class="row g-4 mt-1">
                    <!-- Cột trái: Tải ảnh & Quét OCR -->
                    <div class="col-lg-6">
                        <ocr-image-uploader
                            :is-agreed.sync="isAgreed"
                            @scanned="handleOcrScanned"
                            @scan-error="handleScanError"
                            @reset="handleResetForm"
                        />
                    </div>

                    <!-- Cột phải: Form thông tin CCCD -->
                    <div class="col-lg-6 mb-5">
                        <div class="bg-gray-100 rounded p-4">
                            <h2 class="h5 fw-semibold mb-3 d-flex align-items-center">
                                <i class="fas fa-id-card mr-2 text-primary" style="font-size: 20px"></i>
                                Thông tin căn cước
                            </h2>

                            <!-- Render danh sách section và trường form tự động -->
                            <div
                                v-for="section in formSections"
                                :key="section.title"
                                class="mb-4"
                            >
                                <h3 class="h6 fw-medium text-dark mb-3 border-bottom pb-2">
                                    {{ section.title }}
                                </h3>
                                <div class="row g-3">
                                    <ocr-form-field
                                        v-for="field in section.fields"
                                        :key="field.key"
                                        :config="field"
                                        v-model="localFormData[field.key]"
                                        :is-scanned="scannedFields[field.key]"
                                        @edited="markAsEdited(field.key)"
                                    />
                                </div>
                            </div>

                            <!-- Nút Lưu thông tin -->
                            <div>
                                <button
                                    @click="handleSubmit(saveForm)"
                                    :disabled="invalid || !isAgreed || isSubmitting"
                                    type="button"
                                    :class="[
                                        'btn btn-block fw-medium d-flex align-items-center justify-content-center',
                                        !invalid && isAgreed && !isSubmitting ? 'btn-primary' : 'btn-secondary',
                                    ]"
                                >
                                    <span
                                        v-if="isSubmitting"
                                        class="spinner-border spinner-border-sm mr-2"
                                        role="status"
                                    ></span>
                                    <span>
                                        {{
                                            isSubmitting
                                                ? "Đang lưu..."
                                                : !isAgreed
                                                ? "Vui lòng tích chọn cam kết thông tin"
                                                : !invalid
                                                ? "Lưu thông tin"
                                                : "Vui lòng nhập đầy đủ thông tin"
                                        }}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </ValidationObserver>
</template>

<script>
import { SAVE_IDENTITY_CARD_INFO } from "@/core/services/store/profile.module";
import { FORM_SECTIONS } from "@/information-card/constants.js";
import OcrImageUploader from "@/information-card/OcrImageUploader.vue";
import OcrFormField from "@/information-card/OcrFormField.vue";

export default {
    name: "IdCardScanner",
    components: {
        OcrImageUploader,
        OcrFormField,
    },
    props: {
        formData: {
            type: Object,
            default: () => ({}),
        },
    },
    watch: {
        formData: {
            handler(newVal) {
                if (newVal) {
                    this.localFormData = {
                        full_name: newVal.full_name || "",
                        id_number: newVal.id_number || "",
                        date_of_birth: newVal.date_of_birth || "",
                        gender: newVal.gender || "",
                        nationality: newVal.nationality || "Việt Nam",
                        place_of_birth: newVal.place_of_birth || newVal.place_of_origin || "",
                        place_of_residence: newVal.place_of_residence || "",
                        expiry_date: newVal.expiry_date || "",
                        issued_date: newVal.issued_date || "",
                        issued_place: newVal.issued_place || "",
                    };
                }
            },
            deep: true,
            immediate: true,
        },
    },
    data() {
        return {
            formSections: FORM_SECTIONS,
            isAgreed: false,
            isSubmitting: false,
            scannedFields: {
                full_name: false,
                id_number: false,
                date_of_birth: false,
                gender: false,
                nationality: false,
                place_of_birth: false,
                place_of_residence: false,
                expiry_date: false,
                issued_date: false,
                issued_place: false,
            },
            localFormData: {
                full_name: "",
                id_number: "",
                date_of_birth: "",
                gender: "",
                nationality: "Việt Nam",
                place_of_birth: "",
                place_of_residence: "",
                expiry_date: "",
                issued_date: "",
                issued_place: "",
            },
        };
    },
    methods: {
        handleOcrScanned({ data }) {
            Object.keys(data).forEach((key) => {
                if (data[key]) {
                    this.localFormData[key] = data[key];
                    this.scannedFields[key] = true;
                }
            });
        },
        handleScanError() {
            this.handleResetForm();
        },
        markAsEdited(field) {
            this.scannedFields[field] = false;
        },
        handleResetForm() {
            this.isAgreed = false;
            Object.keys(this.scannedFields).forEach((key) => {
                this.scannedFields[key] = false;
            });
            Object.keys(this.localFormData).forEach((key) => {
                this.localFormData[key] = key === "nationality" ? "Việt Nam" : "";
            });
        },
        async saveForm() {
            try {
                if (this.localFormData.expiry_date) {
                    const expiryDate = new Date(this.localFormData.expiry_date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    if (expiryDate < today) {
                        this.$message.error("Thẻ CCCD đã hết hạn sử dụng. Vui lòng kiểm tra lại!");
                        return;
                    }
                }

                this.isSubmitting = true;
                const payload = {
                    full_name: this.localFormData.full_name,
                    id_number: this.localFormData.id_number,
                    date_of_birth: this.localFormData.date_of_birth,
                    gender: this.localFormData.gender,
                    nationality: this.localFormData.nationality || "Việt Nam",
                    place_of_origin: this.localFormData.place_of_birth,
                    place_of_residence: this.localFormData.place_of_residence,
                    issued_date: this.localFormData.issued_date,
                    issued_place: this.localFormData.issued_place,
                    expiry_date: this.localFormData.expiry_date,
                };

                const response = await this.$store.dispatch(SAVE_IDENTITY_CARD_INFO, payload);
                this.$message({
                    type: "success",
                    message: "Lưu thông tin căn cước thành công!",
                    showClose: true,
                });
                this.$emit("completed", response.data || response);
            } catch (error) {
                console.error("❌ Lỗi khi lưu thông tin:", error);
                const validationErrors =
                    error?.data?.data?.message_validate_form || error?.data?.message_validate_form;
                if (validationErrors && this.$refs.form) {
                    this.$refs.form.setErrors(validationErrors);
                }
                this.$message.error(error?.data?.message || error?.message || "Lưu thông tin thất bại, vui lòng thử lại.");
            } finally {
                this.isSubmitting = false;
            }
        },
    },
};
</script>
